import React, { useState, useMemo } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Container, Paper } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createCustomTheme } from './theme';
import DashboardLayout from './components/Layout/DashboardLayout';
import DecisionTree from './components/DecisionTree/DecisionTree';
import History from './components/History/History';
import Login from './components/Login/Login';
import UserManagement from './components/UserManagement/UserManagement';
import Profile from './components/Profile/Profile';
import Settings from './components/Settings/Settings';
import Reports from './components/Reports/Reports';
import { FlowList } from './pages/FlowList';
import { FlowEditor } from './pages/FlowEditor';
import { FlowPreview } from './pages/FlowPreview';
import { QuestionBank } from './pages/QuestionBank';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FlowNew } from './pages/FlowNew';
import GlobalVariableMappings from './pages/GlobalVariableMappings';

// Componente para verificar autenticação
const PrivateRoute = ({ children }) => {
    const token = localStorage.getItem('token');
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

// Componente para renderizar o conteúdo baseado na navegação
const PageContent = () => {
    const { currentPage } = useNavigation();
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

    // Componente placeholder para páginas em desenvolvimento
    const PlaceholderPage = ({ title }) => (
        <Box sx={{ 
            textAlign: 'center', 
            padding: '2rem',
            '& h2': {
                color: 'primary.main',
                marginBottom: '1rem'
            }
        }}>
            <Typography variant="h2" component="h2">
                {title}
            </Typography>
            <Typography variant="body1">
                Esta página está em desenvolvimento.
            </Typography>
        </Box>
    );

    // Verifica se o usuário tem permissão para acessar a página de usuários
    if (currentPage === 'usuarios' && !userInfo.is_admin) {
        return <Navigate to="/history" replace />;
    }

    // Verifica se o usuário tem permissão para acessar a página de configurações
    if (currentPage === 'configuracoes' && !userInfo.is_admin) {
        return <Navigate to="/history" replace />;
    }

    switch (currentPage) {
        case 'nova-analise':
            return <DecisionTree />;
        case 'historico':
            return <History />;
        case 'flows':
            return <FlowList />;
        case 'question-bank':
            return <QuestionBank />;
        case 'relatorios':
            return <Reports />;
        case 'usuarios':
            return <UserManagement />;
        case 'configuracoes':
            return <Settings />;
        case 'perfil':
            return <Profile />;
        case 'global-variable-mappings':
            return <GlobalVariableMappings />;
        default:
            return <History />;
    }
};

// Componente que envolve o DashboardLayout com autenticação
const ProtectedDashboard = ({ children, toggleColorMode }) => {
    return (
        <PrivateRoute>
            <DashboardLayout toggleColorMode={toggleColorMode}>
                {children}
            </DashboardLayout>
        </PrivateRoute>
    );
};

function App() {
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('themeMode');
        return savedMode || 'light';
    });
    const theme = useMemo(() => createCustomTheme(mode), [mode]);

    const toggleColorMode = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };

    return (
        <BrowserRouter>
            <NavigationProvider>
                <ThemeProvider theme={theme}>
                    <NotificationProvider>
                        <CssBaseline />
                        <Routes>
                            <Route path="/login" element={<Login toggleColorMode={toggleColorMode} />} />
                            <Route path="/" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <DecisionTree />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowList />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows/new" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowEditor />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows/:id/edit" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowEditor />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows/:id/preview" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowPreview />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows-new" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowList />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows-new/new" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowNew />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows-new/:id/edit" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowNew />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows-new/:id/preview" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowPreview />
                                </ProtectedDashboard>
                            } />
                            <Route path="/flows-new/:id" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <FlowNew />
                                </ProtectedDashboard>
                            } />
                            <Route path="/question-bank" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <QuestionBank />
                                </ProtectedDashboard>
                            } />
                            <Route path="/global-variable-mappings" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <GlobalVariableMappings />
                                </ProtectedDashboard>
                            } />
                            <Route path="/*" element={
                                <ProtectedDashboard toggleColorMode={toggleColorMode}>
                                    <PageContent />
                                </ProtectedDashboard>
                            } />
                        </Routes>
                    </NotificationProvider>
                </ThemeProvider>
            </NavigationProvider>
        </BrowserRouter>
    );
}

export default App; 